// R 型タブ（検出数の推移グラフ / 時系列の一覧 / 図面）で、自社 R 型センサーと
// RatSense を種別ごとに分けて並べるための部品。
// 3 画面が同じ並べ方をする必要があるため、画面側ではなくここに置く。
// 表示名は呼び出し側から渡す（libs は Vue に依存しない方針のため、
// mixins/sensorTypes.js の翻訳インスタンスをここからは参照しない）。
//
// 本チケット時点では呼び出し元が無い。後続チケット
// （#114499 検出数の推移グラフ / #114500 時系列の一覧 / #114501 図面）が利用する。
import { SENSOR_TYPES } from '@/mixins/sensorTypes';

// R 型タブに並べるセンサー種別。表示順もこの並びに従う。
const SECTION_TYPES = [SENSOR_TYPES.RAT, SENSOR_TYPES.RAT_SENSE];

const NOTICE = {
  ja: 'RatSense の検出結果は前日分までの表示です',
  en: 'RatSense detections are shown up to the previous day',
};

// 休止中は一覧の後ろに回す。監視中のモニタリングポイントを先に見せるため。
const inactiveOrder = sensor => (sensor.status === 'inactive' ? 1 : 0);

const sortSensors = (sensors, { inactiveLast }) => {
  const byName = [...sensors].sort((a, b) => a.name.localeCompare(b.name));
  if (!inactiveLast) return byName;
  // Array#sort は安定なので、同じ状態どうしの名前順は保たれる
  return byName.sort((a, b) => inactiveOrder(a) - inactiveOrder(b));
};

/**
 * センサーを種別ごとのセクションに分ける。
 *
 * @param {Array<{id: string, name: string, type: string}>} sensors センサーの配列
 * @param {Object} [options]
 * @param {Array<string>} [options.entityIds] 設置場所に登録されたセンサーID。省略時は絞り込まない
 * @param {Object} [options.typeNames] 種別の表示名（例 `{ rat_monitor: 'R型' }`）
 * @param {boolean} [options.inactiveLast] 休止中のセンサーを各グループの末尾に回す。省略時は名前順のみ
 * @returns {Array<{type: string, label: string, sensors: Array}>}
 *          R型 → RatSense の順。センサーが無いセクションは含まない
 */
export const buildSensorSections = (
  sensors,
  { entityIds, typeNames = {}, inactiveLast = false } = {},
) => (
  SECTION_TYPES
    .map(type => ({
      type,
      label: typeNames[type] || type,
      sensors: sortSensors(
        sensors
          .filter(sensor => sensor.type === type)
          .filter(sensor => !entityIds || entityIds.includes(sensor.id)),
        { inactiveLast },
      ),
    }))
    .filter(section => section.sensors.length > 0)
);

/**
 * セクションを選択ボックスの項目に変換する。見出しを項目の間に挟む。
 *
 * @param {Array} sections `buildSensorSections` の戻り値
 * @param {Object} [options]
 * @param {string} [options.allLabel] 先頭に置く「すべて」の表示名。省略時は置かない
 * @returns {Array} 選択ボックスに渡す項目の配列
 */
export const toSensorSelectItems = (sections, { allLabel } = {}) => {
  const items = allLabel ? [{ text: allLabel, value: null }] : [];

  sections.forEach((section) => {
    items.push({ header: section.label });
    section.sensors.forEach(sensor => items.push({ text: sensor.name, value: sensor.id }));
  });

  return items;
};

/**
 * セクションを 1 本のセンサー配列に戻す。
 * 検出結果の取得など、見出しを必要としない場面で使う。
 *
 * @param {Array} sections `buildSensorSections` の戻り値
 * @returns {Array} センサーの配列
 */
export const flattenSections = sections => sections.flatMap(section => section.sensors);

/**
 * RatSense の検出結果が前日分までしか出ないことを伝える文言を返す。
 *
 * @param {string} locale 表示言語（'ja' | 'en'）
 * @returns {string}
 */
export const ratSenseDetectionNotice = locale => NOTICE[locale] || NOTICE.en;
